import { JobWorkChallanData } from '../components/jobwork/types';
import { JobWorkReceipt } from '../components/jobwork/types';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

// ─── HTML to PDF text helper ───────────────────────────────────────────────────

/** Convert HTML (from Quill editor) to plain text lines with bullet/number prefixes */
function htmlToPlainLines(html: string): string[] {
  const lines: string[] = [];
  // Replace <br> variants with newlines
  let text = html.replace(/<br\s*\/?>/gi, '\n');
  // Handle ordered/unordered lists
  let olCounter = 0;
  text = text.replace(/<ol>/gi, () => { olCounter = 0; return ''; });
  text = text.replace(/<\/ol>/gi, '');
  text = text.replace(/<ul>/gi, '');
  text = text.replace(/<\/ul>/gi, '');
  text = text.replace(/<li>([\s\S]*?)<\/li>/gi, (_m, content) => {
    olCounter++;
    // Detect if inside an ordered list by checking if olCounter was reset
    const stripped = content.replace(/<[^>]+>/g, '').trim();
    return `  • ${stripped}\n`;
  });
  // Strip remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');
  // Decode HTML entities
  text = text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ');
  // Split into lines and filter empties
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (trimmed) lines.push(trimmed);
  }
  return lines;
}

// ─── Purchase Order PDF ────────────────────────────────────────────────────────

export interface POPdfItem {
  material_type: string;
  material_quality?: string | null;
  description?: string | null;
  quantity: number;
  uom: string;
  rate: number;
  amount: number;
  image_url?: string | null;
  is_sample?: boolean;
  status?: string;
}

export interface POPdfDesignTag {
  design_no: string;
  image_url?: string | null;
}

export interface POPdfData {
  po_number: string;
  status: string;
  created_at: string;
  valid_until?: string | null;
  created_by?: string;
  notes?: string | null;
  terms?: string | null;
  vendor?: {
    registered_name: string;
    alias?: string | null;
    phone?: string;
    address?: string | null;
    gst?: string | null;
  };
  items?: POPdfItem[];
  design_tags?: POPdfDesignTag[];
  companyDetails?: {
    brandName?: string;
    name?: string;
    address?: string;
    contactNumber?: string;
  };
}

/**
 * Generates a PDF for a Purchase Order using the same styling as the Job Work Challan.
 */
export const generatePurchaseOrderPdf = async (data: POPdfData): Promise<void> => {
  try {
    const doc = new jsPDF();

    // ── Header: Logo (centered) ──────────────────────────────────────────────
    const logoWidth = 70;
    const logoHeight = 14;
    const logoX = (210 - logoWidth) / 2;
    const logoY = 12;

    try {
      doc.addImage('/Peachmode.png', 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch {
      // Fallback to text
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text(
        (data.companyDetails?.brandName || 'PURCHASE ORDER').toUpperCase(),
        105,
        18,
        { align: 'center' }
      );
    }

    // ── Company details below logo ───────────────────────────────────────────
    let companyY = logoY + logoHeight + 2;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    if (
      data.companyDetails?.name &&
      data.companyDetails.name.trim().toLowerCase() !==
        (data.companyDetails?.brandName || '').trim().toLowerCase()
    ) {
      doc.setFontSize(9);
      doc.text(data.companyDetails.name, 105, companyY, { align: 'center' });
      companyY += 4;
    }

    if (data.companyDetails?.address) {
      doc.setFontSize(8);
      const splitAddr = doc.splitTextToSize(data.companyDetails.address, 170);
      splitAddr.forEach((line: string) => {
        doc.text(line, 105, companyY, { align: 'center' });
        companyY += 4;
      });
    }

    if (data.companyDetails?.contactNumber) {
      doc.setFontSize(8);
      doc.text('Contact: ' + data.companyDetails.contactNumber, 105, companyY, { align: 'center' });
      companyY += 4;
    }

    // ── First separator ──────────────────────────────────────────────────────
    const sep1Y = companyY + 2;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(14, sep1Y, 196, sep1Y);

    // ── Document title ───────────────────────────────────────────────────────
    let y = sep1Y + 7;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('PURCHASE ORDER', 105, y, { align: 'center' });

    // ── PO number (left) / Date (right) ─────────────────────────────────────
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('PO No:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(data.po_number, 30, y);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Date:', 145, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(format(new Date(data.created_at), 'dd/MM/yyyy'), 159, y);

    // ── Valid Until (right side, second row) ─────────────────────────────────
    if (data.valid_until) {
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Valid Until:', 145, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(format(new Date(data.valid_until), 'dd/MM/yyyy'), 171, y);
      y -= 5; // restore y to same row level for left column to continue
    }

    // ── Second (thin) separator ──────────────────────────────────────────────
    y += 8;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(14, y, 196, y);

    // ── Vendor details (left column only, full width) ────────────────────────
    y += 6;
    doc.setFontSize(9);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Vendor:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(
      data.vendor?.registered_name ||
        (data.vendor?.alias ? `(${data.vendor.alias})` : 'N/A'),
      36, y
    );

    if (data.vendor?.address) {
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Address:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const splitVAddr = doc.splitTextToSize(data.vendor.address, 130);
      splitVAddr.forEach((line: string, i: number) => {
        doc.text(line, 36, y + i * 4.5);
      });
      y += Math.max(0, (splitVAddr.length - 1) * 4.5);
    }

    if (data.vendor?.gst) {
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('GST:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(data.vendor.gst, 36, y);
    }

    if (data.design_tags && data.design_tags.length > 0) {
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Designs:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(data.design_tags.map(t => t.design_no).join(', '), 36, y);
    }

    // ── Items table ──────────────────────────────────────────────────────────
    y += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Items', 14, y);

    const commonHeadStyles = {
      fillColor: [220, 220, 220] as [number, number, number],
      textColor: [30, 30, 30] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9,
      cellPadding: 3,
    };
    const commonBodyStyles = {
      fontSize: 9,
      cellPadding: 3,
      textColor: [50, 50, 50] as [number, number, number],
    };
    const commonFootStyles = {
      fillColor: [240, 240, 240] as [number, number, number],
      textColor: [30, 30, 30] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9,
    };

    const items = data.items || [];

    // Check whether any item actually has an image
    const hasAnyImage = items.some(item => !!item.image_url);

    // Pre-fetch item images only if at least one exists
    const itemImages: (string | null)[] = [];
    for (const item of items) {
      if (hasAnyImage && item.image_url) {
        itemImages.push(await fetchImageAsBase64(item.image_url));
      } else {
        itemImages.push(null);
      }
    }

    // Build table columns conditionally (Photo column only when at least one image exists)
    const tableHead = hasAnyImage
      ? [['Photo', 'Material', 'Quality', 'Description', 'Qty', 'UOM', 'Rate', 'Amount']]
      : [['#', 'Material', 'Quality', 'Description', 'Qty', 'UOM', 'Rate', 'Amount']];

    const tableBody = items.map((item, idx) => [
      hasAnyImage ? idx : (idx + 1),          // image placeholder index OR row number
      item.material_type,
      item.material_quality || '',
      item.description || '',
      item.quantity.toString(),
      item.uom,
      `INR ${item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      `INR ${item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
    ]);

    const totalAmount = items.reduce((s, i) => s + i.amount, 0);

    const colStyles = hasAnyImage
      ? {
          0: { cellWidth: 18, halign: 'center' as const },   // Photo
          1: { cellWidth: 26 },                               // Material
          2: { cellWidth: 24 },                               // Quality
          3: { halign: 'left' as const },                     // Description (auto)
          4: { cellWidth: 16, halign: 'right' as const },     // Qty
          5: { cellWidth: 14, halign: 'center' as const },    // UOM
          6: { cellWidth: 28, halign: 'right' as const },     // Rate
          7: { cellWidth: 28, halign: 'right' as const },     // Amount
        }
      : {
          0: { cellWidth: 10, halign: 'center' as const },    // #
          1: { cellWidth: 26 },                               // Material
          2: { cellWidth: 24 },                               // Quality
          3: { halign: 'left' as const },                     // Description (auto)
          4: { cellWidth: 16, halign: 'right' as const },     // Qty
          5: { cellWidth: 14, halign: 'center' as const },    // UOM
          6: { cellWidth: 32, halign: 'right' as const },     // Rate
          7: { cellWidth: 32, halign: 'right' as const },     // Amount
        };

    const footRow = hasAnyImage
      ? ['', '', '', '', '', '', 'Total:', `INR ${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`]
      : ['', '', '', '', '', '', 'Total:', `INR ${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`];

    (doc as any).autoTable({
      startY: y + 4,
      head: tableHead,
      body: tableBody,
      foot: [footRow],
      headStyles: commonHeadStyles,
      bodyStyles: commonBodyStyles,
      footStyles: commonFootStyles,
      columnStyles: colStyles,
      margin: { left: 14, right: 14 },
      styles: {
        minCellHeight: hasAnyImage ? 18 : 10,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.1,
      didDrawCell: function (cellData: any) {
        // Draw image in column 0 only when hasAnyImage
        if (
          hasAnyImage &&
          cellData.column.index === 0 &&
          typeof cellData.cell.raw === 'number' &&
          cellData.section === 'body'
        ) {
          const img = itemImages[cellData.cell.raw];
          if (img) {
            const imgSize = 14;
            const imgX = cellData.cell.x + (cellData.cell.width - imgSize) / 2;
            const imgY = cellData.cell.y + 2;
            doc.addImage(img, 'JPEG', imgX, imgY, imgSize, imgSize);
          }
        }
      },
    });

    // ── Notes / Terms ────────────────────────────────────────────────────────
    let finalY = (doc as any).lastAutoTable.finalY + 10;

    if (data.notes && String(data.notes).trim()) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 62, 80);
      doc.text('Notes:', 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      const splitNotes = doc.splitTextToSize(String(data.notes), 182);
      doc.text(splitNotes, 14, finalY + 5);
      finalY += 5 + splitNotes.length * 4;
    }

    if (data.terms && String(data.terms).trim()) {
      finalY += 4;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 62, 80);
      doc.text('Terms & Conditions:', 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      const splitTerms = doc.splitTextToSize(String(data.terms), 182);
      doc.text(splitTerms, 14, finalY + 5);
      finalY += 5 + splitTerms.length * 4;
    }

    // ── Design Tag Thumbnails ────────────────────────────────────────────────
    const tagsWithImages = (data.design_tags || []).filter(t => !!t.image_url);
    if (data.design_tags && data.design_tags.length > 0) {
      finalY += 8;

      // Section heading
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 62, 80);
      doc.text('Tagged Designs', 14, finalY);
      finalY += 5;

      // Pre-fetch all design images in parallel
      const designImagePromises = data.design_tags.map(tag =>
        tag.image_url ? fetchImageAsBase64(tag.image_url) : Promise.resolve(null)
      );
      const designImages = await Promise.all(designImagePromises);

      const thumbSize = 28;       // image square size (mm)
      const labelH   = 6;        // height reserved for design_no label below image
      const cellW    = thumbSize + 6;  // total cell width including side padding
      const cellH    = thumbSize + labelH + 4;
      const startX   = 14;
      const pageW    = 182;       // usable width (196 - 14)
      const perRow   = Math.floor(pageW / cellW) || 1;

      let col = 0;
      let rowStartY = finalY;

      for (let i = 0; i < data.design_tags.length; i++) {
        const tag = data.design_tags[i];
        const img = designImages[i];

        const cellX = startX + col * cellW;

        // New page if needed
        if (rowStartY + cellH > 275) {
          doc.addPage();
          rowStartY = 20;
        }

        // Draw image or placeholder box
        if (img) {
          doc.addImage(img, 'JPEG', cellX, rowStartY, thumbSize, thumbSize);
        } else {
          // Light grey placeholder rectangle
          doc.setDrawColor(200, 200, 200);
          doc.setFillColor(245, 245, 245);
          doc.roundedRect(cellX, rowStartY, thumbSize, thumbSize, 2, 2, 'FD');
          // "No image" text centred in box
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          doc.setTextColor(160, 160, 160);
          doc.text('No image', cellX + thumbSize / 2, rowStartY + thumbSize / 2, { align: 'center' });
        }

        // Design number label below image
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(44, 62, 80);
        doc.text(tag.design_no, cellX + thumbSize / 2, rowStartY + thumbSize + 4, { align: 'center', maxWidth: cellW });

        col++;
        if (col >= perRow) {
          col = 0;
          rowStartY += cellH + 4;
        }
      }

      // Advance finalY past the last row of thumbnails
      if (col > 0) rowStartY += cellH + 4;
      finalY = rowStartY;
    }

    // ── Footer ───────────────────────────────────────────────────────────────
    finalY += 8;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(14, finalY, 196, finalY);

    finalY += 6;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    const footerLeft = `Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`;
    const footerRight = data.created_by ? `Created by: ${data.created_by}` : '';
    doc.text(footerLeft, 14, finalY);
    if (footerRight) doc.text(footerRight, 196, finalY, { align: 'right' });

    finalY += 8;
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text('Powered by stitchflo.app', 105, finalY, { align: 'center' });

    const safeVendor = (data.vendor?.registered_name || data.vendor?.alias || 'vendor')
      .replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`${data.po_number}_${safeVendor}.pdf`);
  } catch (error) {
    console.error('Error generating PO PDF:', error);
  }
};

// TypeScript declaration for VITE_API_URL
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_URL: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// Extend JobWorkChallanData to include companyDetails
export interface JobWorkChallanCompanyDetails {
  brandName?: string;
  name?: string;
  address?: string;
  contactNumber?: string;
}

export interface JobWorkChallanDataWithCompany extends JobWorkChallanData {
  companyDetails?: JobWorkChallanCompanyDetails;
}

const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1';

// Helper to fetch image as base64 via backend proxy
async function fetchImageAsBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(`${BACKEND_BASE_URL}/proxy/proxy-image?url=${encodeURIComponent(imageUrl)}`);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Failed to fetch image via proxy:', err);
    return null;
  }
}

/**
 * Generates a PDF challan for a job work
 * @param data The job work challan data
 * @returns A promise that resolves when the PDF is generated and downloaded
 */
export const generateJobWorkChallan = async (data: JobWorkChallanDataWithCompany & { processMaterial?: boolean; isInternalVendor?: boolean; image1_url?: string; image2_url?: string }): Promise<void> => {
  try {
    console.log('Generating PDF for job work:', data);
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginBottom = 20; // Reserve space at bottom for footer
    const contentBottom = pageHeight - marginBottom;

    // --- Helper: draw footer on every page ---
    const drawPageFooter = () => {
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.2);
        doc.line(14, pageHeight - 14, 196, pageHeight - 14);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text('Powered by stitchflo.app', 105, pageHeight - 9, { align: 'center' });
        doc.text(`Page ${i} of ${totalPages}`, 196, pageHeight - 9, { align: 'right' });
      }
    };

    // --- Helper: check if we need a new page, returns Y position ---
    const ensureSpace = (currentY: number, spaceNeeded: number): number => {
      if (currentY + spaceNeeded > contentBottom) {
        doc.addPage();
        return 20; // top margin on new page
      }
      return currentY;
    };

    // --- HEADER SECTION (Company Details - Center Aligned) ---
    const logoWidth = 70;
    const logoHeight = 14;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 12;

    try {
      doc.addImage('/Peachmode.png', 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (err) {
      console.warn('Could not load logo, falling back to text:', err);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text((data.companyDetails?.brandName || data.companyName || '').toUpperCase(), 105, 18, { align: 'center' });
    }

    let companyDetailsY = logoY + logoHeight + 2;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    if (
      data.companyDetails?.name &&
      data.companyDetails.name.trim().toLowerCase() !== (data.companyDetails?.brandName || data.companyName || '').trim().toLowerCase()
    ) {
      doc.text(data.companyDetails.name, 105, companyDetailsY, { align: 'center' });
      companyDetailsY += 4;
    }

    if (data.companyDetails?.address) {
      doc.setFontSize(8);
      const splitAddress = doc.splitTextToSize(data.companyDetails.address, 170);
      splitAddress.forEach((line: string) => {
        doc.text(line, 105, companyDetailsY, { align: 'center' });
        companyDetailsY += 4;
      });
    }

    if (data.companyDetails?.contactNumber) {
      doc.setFontSize(8);
      doc.text('Contact: ' + data.companyDetails.contactNumber, 105, companyDetailsY, { align: 'center' });
      companyDetailsY += 4;
    }

    // --- FIRST SEPARATOR LINE ---
    const lineY = companyDetailsY + 2;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(14, lineY, 196, lineY);

    // --- JOB WORK NUMBER (left) AND DATE (right) ---
    let detailsY = lineY + 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text(`Challan No: ${data.jobWorkCode}`, 14, detailsY);
    doc.text(`Date: ${data.issueDate}`, 196, detailsY, { align: 'right' });

    // --- SECOND SEPARATOR LINE ---
    detailsY += 4;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(14, detailsY, 196, detailsY);

    // --- JOB DETAILS AND DESIGN IMAGE SECTION ---
    detailsY += 6;
    const sectionStartY = detailsY;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Vendor:', 14, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(data.jobberName || 'N/A', 36, detailsY);

    detailsY += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Job Type:', 14, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(data.jobType?.name || 'N/A', 36, detailsY);

    detailsY += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Expected:', 14, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(data.expectedReturn || 'N/A', 36, detailsY);

    // --- DESIGN IMAGE (right-aligned) ---
    const designImgWidth = 32;
    const designImgHeight = 40;
    const designImgX = 158;
    const designImgY = sectionStartY - 3;
    let designImageShown = false;
    if (data.designImage && typeof data.designImage === 'string') {
      const base64Img = await fetchImageAsBase64(data.designImage);
      if (base64Img) {
        try {
          doc.addImage(base64Img, 'JPEG', designImgX, designImgY, designImgWidth, designImgHeight);
          designImageShown = true;
        } catch (err) {
          console.warn('Could not add design image to PDF:', err);
        }
      }
    }
    if (!designImageShown) {
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(200, 200, 200);
      doc.rect(designImgX, designImgY, designImgWidth, designImgHeight, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('No Image', designImgX + designImgWidth / 2, designImgY + designImgHeight / 2, { align: 'center', baseline: 'middle' });
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text(data.designNo || 'N/A', designImgX + designImgWidth / 2, designImgY + designImgHeight + 4, { align: 'center' });

    detailsY = Math.max(detailsY + 6, designImgY + designImgHeight + 8);

    // Common table styles
    const commonHeadStyles = {
      fillColor: [220, 220, 220] as [number, number, number],
      textColor: [30, 30, 30] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9,
      cellPadding: 3,
      minCellHeight: 8
    };
    const commonBodyStyles = {
      fontSize: 9,
      cellPadding: 3,
      textColor: [50, 50, 50] as [number, number, number]
    };
    const commonFootStyles = {
      fillColor: [240, 240, 240] as [number, number, number],
      textColor: [30, 30, 30] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9
    };

    // --- MATERIALS TABLE (only if materials exist) ---
    if (data.materials && data.materials.length > 0) {
    detailsY += 6;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Materials Issued', 14, detailsY);
    doc.setFont('helvetica', 'normal');

    const materialImages: (string | null)[] = [];
    for (const material of data.materials) {
      if (material.materialImage) {
        materialImages.push(await fetchImageAsBase64(material.materialImage));
      } else {
        materialImages.push(null);
      }
    }

    const showMaterialRates = data.materials.some(m => m.rate > 0);
    const materialTableHead = [
      showMaterialRates
        ? ['Sample', 'Code', 'Type', 'Quantity', 'UOM', 'Rate', 'Amount']
        : ['Sample', 'Code', 'Type', 'Quantity', 'UOM']
    ];
    const materialComments = data.materials.map(m => m.comments || '');

    const materialTableBody = data.materials.map((material, idx) => {
      const base = [
        idx,
        material.materialCode || 'N/A',
        material.materialType || 'N/A',
        material.quantity.toString(),
        material.uom
      ] as (string | number)[];
      if (showMaterialRates) {
        base.push(`INR ${material.rate.toFixed(2)}`);
        base.push(`INR ${(material.quantity * material.rate).toFixed(2)}`);
      }
      return base;
    });

    (doc as any).autoTable({
      startY: detailsY + 5,
      head: materialTableHead,
      body: materialTableBody,
      headStyles: commonHeadStyles,
      bodyStyles: commonBodyStyles,
      foot: showMaterialRates
        ? [[
          '', '', '', '', 'Total:',
          `INR ${data.materials.reduce((sum, m) => sum + (m.quantity * m.rate), 0).toFixed(2)}`
        ]]
        : undefined,
      footStyles: commonFootStyles,
      columnStyles: showMaterialRates ? {
        0: { cellWidth: 22, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 28, halign: 'right' },
        6: { cellWidth: 28, halign: 'right' }
      } : {
        0: { cellWidth: 25, halign: 'center' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' }
      },
      margin: { left: 14, right: 14, bottom: marginBottom },
      showHead: 'everyPage',
      didDrawCell: function (cellData: any) {
        if (cellData.column.index === 0 && typeof cellData.cell.raw === 'number' && cellData.section === 'body') {
          const idx = cellData.cell.raw;
          const img = materialImages[idx];
          if (img) {
            const imgSize = 16;
            const imgX = cellData.cell.x + (cellData.cell.width - imgSize) / 2;
            const imgY = cellData.cell.y + 2;
            doc.addImage(img, 'JPEG', imgX, imgY, imgSize, imgSize);
          }
        }
        if (cellData.column.index === 3 && cellData.section === 'body') {
          const idx = cellData.row.index;
          const comment = materialComments[idx];
          if (comment) {
            const currentFontSize = doc.getFontSize();
            const currentFontStyle = doc.getFont().fontStyle;
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(120, 120, 120);
            const commentY = cellData.cell.y + cellData.cell.height - 2;
            doc.text(comment, cellData.cell.x + cellData.cell.width / 2, commentY, { align: 'center' });
            doc.setFontSize(currentFontSize);
            doc.setFont('helvetica', currentFontStyle || 'normal');
            doc.setTextColor(50, 50, 50);
          }
        }
      },
      styles: {
        minCellHeight: 20,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [252, 252, 252]
      },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.1
    });
    } // end materials conditional

    // --- DESIGN DETAILS TABLE (only if design details exist) ---
    if (data.designDetails && data.designDetails.length > 0) {
      let currentY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 12 : detailsY + 6;
      currentY = ensureSpace(currentY, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Design Details', 14, currentY);

      const showDesignRates = data.designDetails.some(d => d.rate > 0);
      const totalPieces = data.designDetails.reduce((sum, d) => sum + (d.pieces || 0), 0);

      const designTableData = data.designDetails.map(detail => {
        const base = [
          detail.garmentPart,
          detail.size,
          detail.pieces.toString()
        ] as string[];
        if (showDesignRates) {
          base.push(`INR ${detail.rate.toFixed(2)}`);
          base.push(`INR ${(detail.pieces * detail.rate).toFixed(2)}`);
        }
        return base;
      });

      (doc as any).autoTable({
        startY: currentY + 4,
        head: [showDesignRates ? ['Garment Part', 'Size', 'Pieces', 'Rate', 'Amount'] : ['Garment Part', 'Size', 'Pieces']],
        body: designTableData,
        foot: showDesignRates
          ? [[
              '', '', `Total: ${totalPieces} pcs`, 'Total:',
              `INR ${data.designDetails.reduce((sum, d) => sum + (d.pieces * d.rate), 0).toFixed(2)}`
            ]]
          : [[
              '', `Total: ${totalPieces} pcs`, ''
            ]],
        headStyles: commonHeadStyles,
        bodyStyles: commonBodyStyles,
        footStyles: commonFootStyles,
        columnStyles: showDesignRates ? {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'right' },
          4: { halign: 'right' }
        } : {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' }
        },
        margin: { left: 14, right: 14, bottom: marginBottom },
        showHead: 'everyPage',
        styles: {
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [252, 252, 252]
        },
        tableLineColor: [180, 180, 180],
        tableLineWidth: 0.1
      });
    }

    // --- NOTES SECTION ---
    let finalY = ((doc as any).lastAutoTable?.finalY || detailsY) + 10;
    const notesStr = String(data.notes || '');
    const notesPlain = notesStr.replace(/<[^>]+>/g, '').trim();
    if (notesPlain) {
      finalY = ensureSpace(finalY, 20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 62, 80);
      doc.text('Notes:', 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      const noteLines = htmlToPlainLines(notesStr);
      let noteY = finalY + 5;
      for (const line of noteLines) {
        const wrapped = doc.splitTextToSize(line, 182);
        noteY = ensureSpace(noteY, wrapped.length * 4 + 2);
        doc.text(wrapped, 14, noteY);
        noteY += wrapped.length * 4;
      }
      finalY = noteY;
    }

    // --- ATTACHED IMAGES SECTION ---
    const attachedImages: string[] = [];
    if (data.image1_url) {
      const img1 = await fetchImageAsBase64(data.image1_url);
      if (img1) attachedImages.push(img1);
    }
    if (data.image2_url) {
      const img2 = await fetchImageAsBase64(data.image2_url);
      if (img2) attachedImages.push(img2);
    }
    if (attachedImages.length > 0) {
      const imgW = 32;
      const imgH = 40;
      finalY = ensureSpace(finalY, imgH + 16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 62, 80);
      doc.text('Attachments', 14, finalY);
      finalY += 6;
      attachedImages.forEach((img, idx) => {
        const imgX = 14 + idx * (imgW + 8);
        try {
          doc.addImage(img, 'JPEG', imgX, finalY, imgW, imgH);
        } catch {
          // skip if image fails
        }
      });
      finalY += imgH + 6;
    }

    // --- TERMS & CONDITIONS ---
    finalY = ensureSpace(finalY + 8, 20);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(14, finalY, 196, finalY);

    finalY += 6;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text('Generated on: ' + format(new Date(), 'dd/MM/yyyy HH:mm'), 14, finalY);

    finalY += 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text('Terms & Conditions:', 14, finalY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Materials provided must be returned within the expected return date. The jobber will be responsible for any damage or loss.', 14, finalY + 4);

    // --- Draw footer on all pages ---
    drawPageFooter();

    doc.save(`${data.designNo}_${data.jobWorkCode}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

/**
 * Generates a PDF receipt for a job work receipt
 * @param data The job work receipt data
 * @param jobWorkCode The job work code
 * @param vendorName The vendor name
 * @param designNo The design number
 * @param jobType The job type
 * @returns A promise that resolves when the PDF is generated and downloaded
 */
export const generateJobWorkReceipt = async (
  data: JobWorkReceipt,
  jobWorkCode: string,
  vendorName: string,
  designNo: string,
  jobType: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;

      // Add company header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('FACTORY APP', pageWidth / 2, margin + 10, { align: 'center' });

      // Add job work details
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Job Work Code: ${jobWorkCode}`, margin, margin + 30);
      doc.text(`Vendor: ${vendorName}`, margin, margin + 40);
      doc.text(`Design No: ${designNo}`, margin, margin + 50);
      doc.text(`Job Type: ${jobType}`, margin, margin + 60);

      // Add receipt details
      doc.text(`Receipt Date: ${format(new Date(data.receiveDate), 'dd/MM/yyyy')}`, margin, margin + 70);
      if (data.invoiceNumber) {
        doc.text(`Invoice Number: ${data.invoiceNumber}`, margin, margin + 80);
      }
      if (data.invoiceDate) {
        doc.text(`Invoice Date: ${format(new Date(data.invoiceDate), 'dd/MM/yyyy')}`, margin, margin + 90);
      }
      
      // Add received items table
      doc.text('Received Items', margin + 100, margin + 30);
      
      // Log each received item individually for better debugging
      console.log('Individual received items:');
      if (data.receivedItems && Array.isArray(data.receivedItems)) {
        data.receivedItems.forEach((item, index) => {
          console.log(`Item ${index}:`, JSON.stringify(item, null, 2));
        });
      }
      
      // If invoice amount is provided, use it directly
      let manualInvoiceAmount = 0;
      if (typeof data.invoiceAmount === 'number' && !isNaN(data.invoiceAmount)) {
        manualInvoiceAmount = data.invoiceAmount;
        console.log('Using provided invoice amount:', manualInvoiceAmount);
      }
      
      // Ensure receivedItems is array and contains data
      if (!data.receivedItems || !Array.isArray(data.receivedItems)) {
        console.warn('No valid receivedItems array found in receipt');
        data.receivedItems = [];
      }
      
      // Combine items by garment part and size, summing their quantities
      const consolidatedItems = new Map();
      
      data.receivedItems.forEach(item => {
        // Create a unique key for each combination of garment part and size
        const key = `${item.garmentPart || ''}|${item.size || ''}`;
        
        if (!consolidatedItems.has(key)) {
          // Get the rate - use the invoice amount if present and there's only one item
          let rate = getItemRate(item);
          
          // If we have a manual invoice amount and only one type of item, calculate the rate
          if (manualInvoiceAmount > 0 && data.receivedItems.length === 1) {
            const qty = getItemQuantity(item);
            if (qty > 0) {
              rate = manualInvoiceAmount / qty;
              console.log(`Calculated rate from invoice amount: ${rate}`);
            }
          }
          
          consolidatedItems.set(key, {
            garmentPart: item.garmentPart || '-',
            size: item.size || '-',
            quantity: 0,
            rate: rate,
            uom: getItemUom(item)
          });
        }
        
        // Add this item's quantity to the consolidated entry
        const entry = consolidatedItems.get(key);
        const itemQuantity = getItemQuantity(item);
        console.log(`Adding quantity ${itemQuantity} to entry:`, entry);
        entry.quantity += itemQuantity;
      });
      
      console.log('Consolidated items:', Array.from(consolidatedItems.values()));
      
      // If we have zero quantities but a manual invoice amount, set a default quantity
      if (manualInvoiceAmount > 0) {
        const items = Array.from(consolidatedItems.values());
        const allZeroQuantities = items.every(item => item.quantity === 0);
        
        if (allZeroQuantities && items.length > 0) {
          console.log('Setting default quantity of 1 since we have invoice amount but zero quantities');
          items[0].quantity = 1;
          items[0].rate = manualInvoiceAmount;
          // Update the map
          consolidatedItems.clear();
          const key = `${items[0].garmentPart}|${items[0].size}`;
          consolidatedItems.set(key, items[0]);
        }
      }
      
      // Convert map to array for table rendering
      const tableData = Array.from(consolidatedItems.values()).map(item => {
        // Ensure quantity is a number and convert to string
        const qtyStr = item.quantity.toString();
        // Calculate amount manually
        const amount = item.quantity * item.rate;
        
        return [
          item.garmentPart,
          item.size,
          qtyStr, // Display quantity as string
          item.uom,
          `INR ${item.rate.toFixed(2)}`,
          `INR ${amount.toFixed(2)}`
        ];
      });
      
      // If we have no items after consolidation, add a placeholder row
      if (tableData.length === 0) {
        tableData.push(['No items', '-', '0', 'PCS', 'INR 0.00', 'INR 0.00']);
      }
      
      // Calculate the total amount from consolidated items
      const itemsTotal = Array.from(consolidatedItems.values()).reduce(
        (sum, item) => sum + (item.quantity * item.rate), 
        0
      );
      
      (doc as any).autoTable({
        startY: 100,
        head: [['Garment Part', 'Size', 'Quantity', 'UOM', 'Rate', 'Amount']],
        body: tableData,
        foot: [[
          '', '', '', '', 'Total:', 
          `INR ${itemsTotal.toFixed(2)}`
        ]],
        headStyles: { fillColor: [52, 152, 219], textColor: 255 },
        footStyles: { fillColor: [240, 240, 240], textColor: [44, 62, 80], fontStyle: 'bold' }
      });
      
      // Add tax and invoice details
      const currentY = (doc as any).lastAutoTable.finalY + 10;
      
      // Subtotal is the sum of the amounts from the table items
      const subtotal = itemsTotal;
      
      // Get tax amount, default to 0
      const taxAmount = (typeof data.taxAmount === 'number' && !isNaN(data.taxAmount)) 
        ? data.taxAmount 
        : 0;
      
      // Calculate total
      const total = subtotal + taxAmount;
      
      // Create a simple summary table with improved styling
      const summaryData = [
        ['Subtotal', `INR ${subtotal.toFixed(2)}`],
        ['Tax', `INR ${taxAmount.toFixed(2)}`],
        ['Total', `INR ${total.toFixed(2)}`]
      ];
      
      (doc as any).autoTable({
        startY: currentY,
        body: summaryData,
        theme: 'plain',
        styles: {
          cellPadding: 2,
          fontSize: 11
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 100, textColor: [44, 62, 80] },
          1: { halign: 'right', cellWidth: 50, textColor: [44, 62, 80] }
        },
        margin: { left: 125 }
      });

      // Add footer
      const finalY = (doc as any).lastAutoTable.finalY + 15;
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Generated on: ' + format(new Date(), 'dd/MM/yyyy'), 20, finalY);
      
      // Save the PDF
      const safeDateStr = data.receiveDate.replace(/\//g, '-');
      doc.save(`Receipt_${jobWorkCode}_${safeDateStr}.pdf`);
      
      resolve();
    } catch (error) {
      console.error('Error generating receipt PDF:', error);
      reject(error);
    }
  });
};

// Helper functions to safely extract values from different item structures
function getItemQuantity(item: any): number {
  // Debug log to inspect this specific item
  console.log('Getting quantity for item:', JSON.stringify(item, null, 2));
  
  // Try different property names that might contain quantity
  if (typeof item.pieceCount === 'number' && !isNaN(item.pieceCount)) {
    console.log('Using pieceCount:', item.pieceCount);
    return item.pieceCount;
  }
  
  // Use a type-safe check for 'quantity' property to avoid linter errors
  if (item && typeof (item as any).quantity === 'number' && !isNaN((item as any).quantity)) {
    console.log('Using quantity:', (item as any).quantity);
    return (item as any).quantity;
  }
  
  if (typeof item.pieces === 'number' && !isNaN(item.pieces)) {
    console.log('Using pieces:', item.pieces);
    return item.pieces;
  }
  
  // If all fields are undefined or NaN, manually check for the most likely field
  // and force conversion to number
  if (item.pieceCount !== undefined) {
    const num = Number(item.pieceCount);
    if (!isNaN(num)) {
      console.log('Force converted pieceCount:', num);
      return num;
    }
  }
  
  // Type-safe check for string conversion
  if (item && (item as any).quantity !== undefined) {
    const num = Number((item as any).quantity);
    if (!isNaN(num)) {
      console.log('Force converted quantity:', num);
      return num;
    }
  }
  
  // Default to 0 if no quantity property found
  console.log('No valid quantity found, returning 0');
  return 0;
}

function getItemRate(item: any): number {
  // Try different property names that might contain rate
  if (typeof item.processingRate === 'number' && !isNaN(item.processingRate)) 
    return item.processingRate;
  
  if (typeof item.rate === 'number' && !isNaN(item.rate)) 
    return item.rate;
  
  // Force conversion for string values
  if (item.processingRate !== undefined) {
    const num = Number(item.processingRate);
    if (!isNaN(num)) return num;
  }
  
  if (item.rate !== undefined) {
    const num = Number(item.rate);
    if (!isNaN(num)) return num;
  }
  
  // Default to 0 if no rate property found
  return 0;
}

function getItemUom(item: any): string {
  // Try different property names that might contain UOM (Unit of Measure)
  if (typeof item.materialUom === 'string' && item.materialUom) return item.materialUom;
  if (typeof item.uom === 'string' && item.uom) return item.uom;
  
  // Default to PCS if no UOM property found
  return 'PCS';
}

/**
 * Generates a PDF report of pending job works for a specific vendor
 */
export interface PendingJobWorkItem {
  jobWorkCode: string;
  designNo: string;
  issueDate: string;
  pendingQty: number;
  unit: string;
  imageUrl?: string | null;
  jobType?: string;
  status?: string;
}

export const generatePendingJobWorksReport = async (
  vendorName: string,
  pendingItems: PendingJobWorkItem[]
): Promise<void> => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // --- HEADER ---
    try {
      const logoWidth = 70;
      const logoHeight = 14;
      const logoX = (pageWidth - logoWidth) / 2;
      doc.addImage('/Peachmode.png', 'PNG', logoX, 10, logoWidth, logoHeight);
    } catch {
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('PEACHMODE', pageWidth / 2, 18, { align: 'center' });
    }

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Pending Job Works Report', pageWidth / 2, 32, { align: 'center' });

    // Vendor name
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Vendor: ${vendorName}`, 14, 40);

    // Date
    doc.text(`Date: ${format(new Date(), 'dd/MM/yyyy')}`, pageWidth - 14, 40, { align: 'right' });

    // Total pending items count
    doc.setFontSize(10);
    const totalPending = pendingItems.reduce((sum, item) => sum + item.pendingQty, 0);
    doc.text(`Total Pending Items: ${pendingItems.length} challans | ${totalPending} pcs/mtr`, 14, 47);

    // Separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(14, 50, pageWidth - 14, 50);

    // Pre-fetch all images
    const imagePromises = pendingItems.map(async (item) => {
      if (item.imageUrl) {
        try {
          return await fetchImageAsBase64(item.imageUrl);
        } catch {
          return null;
        }
      }
      return null;
    });
    const images = await Promise.all(imagePromises);

    // Build table data
    const tableBody = pendingItems.map((item, idx) => [
      idx, // placeholder for image (we draw it in didDrawCell)
      item.jobWorkCode,
      item.designNo,
      item.jobType || '-',
      item.issueDate ? format(new Date(item.issueDate), 'dd/MM/yyyy') : '-',
      `${item.pendingQty} ${item.unit}`,
      item.status?.replace('_', ' ') || '-'
    ]);

    // Draw table using autotable
    (doc as any).autoTable({
      startY: 54,
      head: [['Image', 'Challan No', 'Design No', 'Job Type', 'Issue Date', 'Pending', 'Status']],
      body: tableBody,
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 28, halign: 'center' },
        1: { cellWidth: 30, fontSize: 8 },
        2: { cellWidth: 25, fontSize: 9, fontStyle: 'bold' },
        3: { cellWidth: 22, fontSize: 8 },
        4: { cellWidth: 24, fontSize: 8, halign: 'center' },
        5: { cellWidth: 22, fontSize: 9, fontStyle: 'bold', halign: 'center' },
        6: { cellWidth: 28, fontSize: 8, halign: 'center' }
      },
      styles: {
        minCellHeight: 28,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248]
      },
      margin: { left: 14, right: 14 },
      didDrawCell: function (cellData: any) {
        // Draw images in the first column
        if (cellData.column.index === 0 && typeof cellData.cell.raw === 'number' && cellData.section === 'body') {
          const idx = cellData.cell.raw;
          const img = images[idx];
          if (img) {
            const imgW = 18;
            const imgH = 24;
            const imgX = cellData.cell.x + (cellData.cell.width - imgW) / 2;
            const imgY = cellData.cell.y + (cellData.cell.height - imgH) / 2;
            doc.addImage(img, 'JPEG', imgX, imgY, imgW, imgH);
          }
        }
      }
    });

    // Footer
    const finalY = (doc as any).lastAutoTable?.finalY || 200;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, finalY + 10);

    // Save with vendor name
    const safeVendorName = vendorName.replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`${safeVendorName}_Pending_Jobs_Report.pdf`);
  } catch (error) {
    console.error('Error generating pending job works report:', error);
    throw error;
  }
}; 